/**
 * versona_watch.ts — what is WRONG, for people who do not want to read
 * thirty-one cards (2026-09-20).
 *
 * The Metrics tab answers questions you thought to ask. The watch list answers
 * the one you did not: a strip above the grid that stays put whatever chip is
 * lit, because an alert you have to go and find is not an alert.
 *
 * Pure, and deliberately reading the SAME structured inputs the cards do —
 * never the formatted strings. A rule that parsed "2 of 3 · 67 %" back into a
 * number would break the first time a label was reworded.
 *
 * Every rule states its threshold in its own text, so the strip explains itself
 * and nobody has to come back here to find out what "high" meant.
 */
import { tickStaleAfterS, type WorldMetricsInput } from './versona_metrics_world.js';
import type { MindMetricsInput } from './versona_metrics_mind.js';
import type { MetricCat } from './versona_metrics.js';

export type WatchLevel = 'alert' | 'warn' | 'note';
export interface WatchItem {
  id: string;
  level: WatchLevel;
  /** Which chip to light when the reader clicks through. */
  cat: MetricCat;
  text: string;
  hint?: string;
}

const pct1 = (x: number): string => `${(x * 100).toFixed(0)} %`;
const share = (n: number, d: number): number => (d > 0 ? n / d : 0);

/**
 * `population` and `populationWas` let the strip see a FALL, which is the one
 * thing a single snapshot can never show. `populationWas` is the previous
 * refresh's count, or 0 on the first pass (when no fall is claimed).
 */
export interface WatchInput {
  world: WorldMetricsInput;
  mind: MindMetricsInput;
  population: number;
  populationWas: number;
}

export function computeWatch(inp: WatchInput): WatchItem[] {
  const { world: w, mind: m } = inp;
  const out: WatchItem[] = [];
  const add = (id: string, level: WatchLevel, cat: MetricCat, text: string, hint?: string): void => {
    out.push({ id, level, cat, text, hint });
  };

  // ── The world's pulse. Everything else is meaningless if this is stopped. ──
  if (w.nowMs > 0) {
    for (const t of w.ticks) {
      const age = (w.nowMs - t.lastAtMs) / 1000;
      const limit = tickStaleAfterS(t.gapMicros);
      if (age > limit) {
        add(`tick-${t.name}`, 'alert', 'sim', `${t.name} has not run for ${age.toFixed(0)} s`,
          `it normally runs every ${(t.gapMicros / 1e6).toFixed(1)} s, so anything past ${limit.toFixed(0)} s has stopped — and while it is stopped nothing it drives moves, anywhere in the world`);
      }
    }
  }

  // ── Population ────────────────────────────────────────────────────────────
  if (inp.populationWas > 0 && inp.population < inp.populationWas) {
    const lost = inp.populationWas - inp.population;
    add('pop-fall', lost > inp.populationWas * 0.01 ? 'alert' : 'warn', 'people',
      `population fell by ${lost.toLocaleString('en-US')} since the last refresh`,
      'births are meant to outpace the reaper; a sustained fall means the top-up is not keeping up');
  }

  // ── Government ────────────────────────────────────────────────────────────
  const brokenPromises = w.promises.filter((p) => p.status === 'broken').length;
  if (w.promises.length > 0 && share(brokenPromises, w.promises.length) > 0.5) {
    add('promises', 'warn', 'gov', `${brokenPromises} of ${w.promises.length} federal promises are broken`,
      'over half — a seat that does not keep its word loses legitimacy, and under 25 % only relief can pass');
  }
  const hotRivalries = w.rivalries.filter((r) => r.grievance >= 70).length;
  if (hotRivalries > 0) {
    add('grievance', hotRivalries >= 50 ? 'warn' : 'note', 'gov', `${hotRivalries} rivalries are at grievance 70+`,
      'a committed council declares at this heat — these are the wars waiting to happen');
  }
  const openCases = w.crimes.filter((c) => c.status === 'open').length;
  if (openCases > 50) add('crime-open', 'warn', 'law', `${openCases} crime cases are still open`, 'a backlog the Tribunal is not clearing');

  // ── Economy ───────────────────────────────────────────────────────────────
  const lastEpoch = w.rounds.reduce((a, r) => Math.max(a, r.epoch), -Infinity);
  const now = w.rounds.filter((r) => r.epoch === lastEpoch);
  const vol = now.reduce((a, r) => a + r.volume, 0);
  const unfilled = now.reduce((a, r) => a + r.unfilled, 0);
  if (vol + unfilled > 0 && share(unfilled, vol + unfilled) > 0.4) {
    add('unfilled', 'warn', 'econ', `${pct1(share(unfilled, vol + unfilled))} of demand went unfilled this market round`,
      'over the 40 % mark — the towns are asking for goods nobody is bringing');
  }
  const starved = w.firms.filter((f) => f.shortHours > 0);
  if (starved.length > 0) {
    add('firm-short', 'note', 'space', `${starved.length} space firm${starved.length === 1 ? '' : 's'} sat idle for want of ore`,
      `${starved.reduce((a, f) => a + f.shortHours, 0)} short-hours in total`);
  }
  const drained = w.deposits.filter((d) => d.capacity > 0 && d.remaining / d.capacity < 0.1).length;
  if (drained > 0) add('deposits', 'warn', 'space', `${drained} belt deposit${drained === 1 ? '' : 's'} under 10 % remaining`, 'the ore that feeds the yards is running out');
  const lostShip = w.shipments.filter((s) => s.state === 'lost').length;
  if (w.shipments.length > 0 && share(lostShip, w.shipments.length) > 0.5) {
    add('ambush', 'warn', 'space', `${lostShip} of ${w.shipments.length} shipments were lost to ambush`,
      'over half — the lanes are not survivable without escort');
  }

  // ── Guilds ────────────────────────────────────────────────────────────────
  if (w.guilds.length > 0) {
    const staffed = new Set([...w.guildMembers.map((x) => x.guildId), ...w.guildVersonas.map((x) => x.guildId)]);
    const empty = w.guilds.filter((g) => !staffed.has(g.guildId)).length;
    if (share(empty, w.guilds.length) > 0.5) {
      add('guild-empty', 'note', 'guilds', `${empty} of ${w.guilds.length} guild halls stand empty`,
        'founded and never staffed — over half of them');
    }
  }

  // ── The mind ──────────────────────────────────────────────────────────────
  if (m.moods.length > 0) {
    const below = m.moods.filter((x) => x.p < x.bp).length;
    if (share(below, m.moods.length) > 0.6) {
      add('mood', 'warn', 'mind', `${pct1(share(below, m.moods.length))} of the world is below its own baseline mood`,
        'measured per versona against their own temperament, so this is not just a gloomy population — something is pressing on them');
    }
  }
  if (m.minds.length > 0) {
    const hungry = m.minds.filter((x) => x.hungerStreak > 0).length;
    if (share(hungry, m.minds.length) > 0.25) {
      add('hunger', 'alert', 'mind', `${pct1(share(hungry, m.minds.length))} are carrying a hunger streak`,
        'shortage is biting across more than a quarter of the world');
    }
  }
  if (m.aspirations.length > 0) {
    const stalled = m.aspirations.filter((a) => a.progress === 0).length;
    if (share(stalled, m.aspirations.length) > 0.75) {
      add('aspiration', 'note', 'mind', `${pct1(share(stalled, m.aspirations.length))} of aspirations have made no progress`,
        'they want things and cannot move toward them — worth asking whether the progress hook is wired');
    }
  }
  // Price beliefs against the latest round: a world trading on bad numbers.
  {
    const truthNow = new Map<string, number[]>();
    for (const t of m.truth) {
      if (t.epoch !== lastEpoch) continue;
      const a = truthNow.get(t.commodity) ?? []; a.push(t.avgPrice); truthNow.set(t.commodity, a);
    }
    const held = new Map<string, number[]>();
    for (const b of m.priceBeliefs) { const a = held.get(b.commodity) ?? []; a.push(b.mu); held.set(b.commodity, a); }
    const mean = (a: number[]): number => a.reduce((x, y) => x + y, 0) / a.length;
    let worst = { k: '', err: 0, n: 0 };
    for (const [k, mus] of held) {
      const t = truthNow.get(k);
      if (!t || t.length === 0) continue;
      const actual = mean(t);
      if (actual === 0) continue;
      const err = Math.abs(mean(mus) - actual) / actual;
      if (err > worst.err) worst = { k, err, n: mus.length };
    }
    if (worst.err > 1) {
      add('price-belief', 'note', 'mind', `the world is ${pct1(worst.err)} wrong about the price of ${worst.k}`,
        `${worst.n.toLocaleString('en-US')} versonas hold that belief, and they trade on it rather than on the book price`);
    }
  }

  // Alerts first, then warnings, then notes; stable within a level.
  const rank: Record<WatchLevel, number> = { alert: 0, warn: 1, note: 2 };
  return out.sort((a, b) => rank[a.level] - rank[b.level]);
}
